import { useEffect, useRef, useState } from "react";

/**
 * Infinite-scroll helper.
 * - Resets to `pageSize` whenever `resetKey` changes (e.g. when filters update).
 * - When the sentinel comes into view AND there's more data, simulates a
 *   `loadDelay`-ms fetch and bumps `visibleCount` by `pageSize`.
 */
export function useInfiniteList<T>({
  items,
  pageSize = 6,
  loadDelay = 600,
  resetKey,
}: {
  items: T[];
  pageSize?: number;
  loadDelay?: number;
  resetKey?: unknown;
}) {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Reset when the dataset / filter changes
  useEffect(() => {
    setVisibleCount(pageSize);
    setLoadingMore(false);
  }, [resetKey, pageSize]);

  const visible = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;

  useEffect(() => {
    if (!hasMore) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || loadingMore) return;
        setLoadingMore(true);
        const t = window.setTimeout(() => {
          setVisibleCount((c) => Math.min(c + pageSize, items.length));
          setLoadingMore(false);
        }, loadDelay);
        return () => window.clearTimeout(t);
      },
      { rootMargin: "320px 0px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadingMore, items.length, pageSize, loadDelay]);

  return { visible, hasMore, loadingMore, sentinelRef, total: items.length, visibleCount };
}
